// Simple auth utilities: logout + 401 handling + redirect
import { getApiBase } from '../apiBase';
import { clearParentSession, isParentSessionRequest } from '../parent/parentSession';

const isSmartboardTeacherPath = (path) => /^\/mini-tasks\/board\/[^/]+\/teacher(?:\/|$)/.test(path);
const isSessionCheckPath = (path) => /^\/auth\/session(?:\?|$|-diagnostic)/.test(path);

export function clearSession() {
  clearParentSession();
  try {
    localStorage.removeItem('adminUser');
  } catch (_) {
    // ignore
  }
}

function resolveRedirectPath() {
  try {
    const p = (window.location && window.location.pathname) || '';
    if (p === '/student360' || p.startsWith('/student360/')) return '/student360/expired';
  } catch (_) {
    // ignore
  }
  return '/admin';
}

export function redirectToLogin(reason = 'session_expired') {
  try {
    const targetPath = resolveRedirectPath();
    const url = new URL(window.location.origin + targetPath);
    if (reason) url.searchParams.set('reason', reason);
    // Use assign to keep history; replace would prevent back which might be desired too
    window.location.assign(url.toString());
  } catch (_) {
    // Fallback
    window.location.href = resolveRedirectPath();
  }
}

export function logoutAndRedirect(reason = 'session_expired') {
  clearSession();
  redirectToLogin(reason);
}

// Installs a lightweight wrapper around window.fetch to catch 401
export function installFetch401Interceptor() {
  if (typeof window === 'undefined') return;
  if (!window.fetch) return;
  // Prevent double-wrapping
  if (window.fetch && window.fetch.__kk401Wrapped) return;

  const originalFetch = window.fetch.bind(window);
  const wrapped = async (input, init) => {
    try {
      const base = getApiBase();
      let url;
      if (typeof input === 'string') {
        url = input;
      } else if (input && typeof input.url === 'string') {
        url = input.url;
      } else {
        url = '';
      }

      const isApiCall = (() => {
        if (!url) return false;
        // Absolute API URL
        if (url.startsWith(base)) return true;
        // Same-origin absolute path (starts with '/') to our API prefix
        try {
          if (url.startsWith('/')) return true;
        } catch (_) {}
        return false;
      })();

      // Determine path for allow/deny logic
      const currentPath = (() => {
        try { return window.location && window.location.pathname; } catch (_) { return ''; }
      })() || '';
      const requestPath = (() => {
        try {
          if (!url) return '';
          // If absolute API URL, strip base to get path
          if (url.startsWith(base)) return url.slice(base.length) || '/';
          // If same-origin absolute path, use as-is
          if (url.startsWith('/')) return url;
          return '';
        } catch (_) { return ''; }
      })();
      const isSmartboardTeacherRequest = isSmartboardTeacherPath(requestPath);

      // Clone/init options safely
      const nextInit = { ...(init || {}) };
      // Always send cookies for API calls unless explicitly set
      if (isApiCall && nextInit.credentials === undefined) {
        nextInit.credentials = 'include';
      }
      // Do not inject Authorization header; rely on cookie-only auth

      const resp = await originalFetch(input, nextInit);
      // Handle 401 globally
      if (resp && resp.status === 401) {
        // Skip redirect for superadmin flows and refresh endpoint to avoid breaking login pages
        const isSuperadminContext = currentPath.startsWith('/superadmin');
        const isSuperadminRequest = requestPath.startsWith('/superadmin');
        const isRefreshRequest = requestPath.startsWith('/auth/refresh') || requestPath.startsWith('/auth/parent-session/');
        const isSessionCheck = isSessionCheckPath(requestPath);
        const isParentRequest = isApiCall && isParentSessionRequest(base, url);
        if (isSuperadminContext || isSuperadminRequest || isRefreshRequest || isSmartboardTeacherRequest || isSessionCheck || isParentRequest) {
          return resp;
        }
        clearSession();
        setTimeout(() => redirectToLogin('session_expired'), 0);
      }
      return resp;
    } catch (_) {
      // Fallback to original if wrapper logic throws
      const resp = await originalFetch(input, init);
      try {
        const url = typeof input === 'string' ? input : input?.url || '';
        const base = getApiBase();
        const path = url.startsWith(base) ? url.slice(base.length) : url;
        if (resp && resp.status === 401 && !isSmartboardTeacherPath(path) && !isSessionCheckPath(path)) {
          clearSession();
          setTimeout(() => redirectToLogin('session_expired'), 0);
        }
      } catch {}
      return resp;
    }
  };
  // mark
  wrapped.__kk401Wrapped = true;
  window.fetch = wrapped;
}
